import React, { useEffect, useState, useRef } from 'react';
import { Input, Flex, Spin, Button } from 'antd';
import { PoweroffOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import Search from '@/assets/icons/search-md.svg?react';
import { useUser } from '@/hooks/use-user/use-user';
import { useJWT } from '@/hooks/use-jwt/use-jwt';
import { useLogout } from '@/hooks/use-logout/use-logout';
import styles from './call-taker-layout.module.scss';
import { Button as CustomButton } from '@/components/button/button';

interface CallTakerLayoutProps {
  children?: React.ReactNode;
}

const DEFAULT_COPILOT_PLUGIN_URL = 'https://copilot-stage.voicesphere.io';

/** Undefined rather than a throw, so a malformed env value fails closed. */
const toOrigin = (url: string): string | undefined => {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

export const CallTakerLayout: React.FC<CallTakerLayoutProps> = () => {
  const { t } = useTranslation(['layout', 'common', 'navigation']);
  const [copilotUrl, setCopilotUrl] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { userData } = useUser();
  const { bearerToken } = useJWT();
  const { logout } = useLogout();

  const updateCopilotUrl = (phone: string) => {
    const baseUrl = import.meta.env.VITE_COPILOT_PLUGIN_URL || DEFAULT_COPILOT_PLUGIN_URL;
    const url = new URL(baseUrl);

    url.searchParams.set('phone', phone);
    return url.toString();
  };

  useEffect(() => {
    if (bearerToken) {
      const initialUrl = updateCopilotUrl(phoneNumber);
      setCopilotUrl(initialUrl);
    }
  }, [bearerToken, userData]);

  const handleSearch = () => {
    const cleanPhone = searchValue.trim();
    setPhoneNumber(cleanPhone);

    if (bearerToken) {
      const newUrl = updateCopilotUrl(cleanPhone);
      setCopilotUrl(newUrl);

      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            type: 'PHONE_NUMBER_UPDATE',
            phoneNumber: cleanPhone,
          },
          '*'
        );
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // `VITE_COPILOT_PLUGIN_URL` is a base url, so it may carry a path or a trailing
      // slash; compared raw it would never match `event.origin`.
      const allowedOrigins = [DEFAULT_COPILOT_PLUGIN_URL, import.meta.env.VITE_COPILOT_PLUGIN_URL]
        .filter((url): url is string => Boolean(url))
        .map(toOrigin);

      // Exact match, not `includes`: a substring test with the allowed origin as the
      // needle lets `https://copilot-stage.voicesphere.io.attacker.example` through.
      if (!allowedOrigins.some((origin) => event.origin === origin)) {
        return;
      }

      if (event.data.type === 'COPILOT_READY') {
        if (bearerToken && iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage(
            {
              type: 'AUTH_TOKEN',
              token: bearerToken,
            },
            '*'
          );
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [bearerToken]);

  if (!copilotUrl) {
    return (
      <div className={styles.loadingContainer}>
        <Spin className={styles.spinner} size="large" />
      </div>
    );
  }

  return (
    <div className={styles.callTakerLayout}>
      <div className={styles.searchPanel}>
        <Flex gap={8} className={styles.searchPanelFlex}>
          <Flex className={styles.currentSearch}>
            <Flex gap={8} align="center" className={styles.searchContainer}>
              <Input
                placeholder={t('layout:callTaker.searchPlaceholder')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleKeyPress}
                prefix={
                  <Search className={styles.iconSearch} height={16} width={16} strokeWidth={2} />
                }
                className={styles.searchInput}
              />
              <CustomButton type="primary" onClick={handleSearch} disabled={!searchValue.trim()}>
                {t('common:actions.search')}
              </CustomButton>
            </Flex>

            {phoneNumber && (
              <div className={styles.currentSearch}>
                <Trans
                  ns="layout"
                  i18nKey="callTaker.currentlyViewing"
                  values={{ phoneNumber }}
                  components={{ phone: <span className={styles.phoneNumber} /> }}
                />
              </div>
            )}
          </Flex>

          <Button
            role="button"
            type="link"
            icon={<PoweroffOutlined className={styles.logoutIcon} />}
            onClick={async () => await logout()}
            className={styles.logoutButton}
          >
            <span className={styles.logoutText}>{t('navigation:logout')}</span>
          </Button>
        </Flex>
      </div>

      <iframe
        ref={iframeRef}
        src={copilotUrl}
        className={styles.copilotFrame}
        title={t('layout:callTaker.frameTitle')}
        allow="microphone; camera; geolocation; clipboard-read; clipboard-write"
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-top-navigation"
      />
    </div>
  );
};
